import './Setup.css';

function Setup() {
  return (
    <div className="setup-guide">
      <h2>Astena Integration Hub — Deployment Guide</h2>
      <p className="description">Complete configuration guide for the Astena Integration Hub system</p>

      {/* Architecture Overview */}
      <section className="setup-section">
        <h3>📋 Architecture Overview</h3>
        <div className="architecture-box">
          <div className="arch-item">
            <strong>Frontend:</strong>
            React app hosted on Azure Static Web Apps (or any static host). Users sign in with their Microsoft account (identity only — no Azure management scopes in the browser).
          </div>
          <div className="arch-item">
            <strong>Backend — LogicAppsApi:</strong>
            <ul>
              <li><code>GET /api/runs</code> — returns run stats (total / failed / succeeded) for a workflow in a given time window</li>
              <li><code>GET /api/actions</code> — returns failed step details for a specific run</li>
              <li><code>GET /api/specs</code> — lists all stored API specs (id, label, group, shareId)</li>
              <li>
                <code>POST /api/specs</code> — creates or updates an OpenAPI YAML spec (uses name to match existing specs)
                <br/>
                <strong>Request body:</strong> <code>{'{'} name: "API Name", yaml: "openapi: 3.0.0...", group: "Optional Group" {'}'}</code>
                <br/>
                <strong>Behavior:</strong> If a spec with the same name exists, updates it and preserves the share ID. Otherwise, creates a new spec with a new share ID.
                <br/>
                <strong>Response:</strong> <code>{'{'} id, label, group, fileName, shareId {'}'}</code>
              </li>
              <li><code>GET /api/specs/{'{name}'}</code> — serves a stored YAML spec (authenticated)</li>
              <li><code>DELETE /api/specs/{'{name}'}</code> — deletes spec from blob, table, and shares index</li>
              <li><code>GET /api/shares/{'{id}'}</code> — resolves a share ID to its spec metadata (anonymous)</li>
              <li><code>GET /api/shares/{'{id}'}/spec</code> — serves the YAML for a shared spec (anonymous)</li>
            </ul>
            Authenticates to Azure using its own <strong>Managed Identity</strong> — no secrets required.
          </div>
          <div className="arch-item">
            <strong>Azure Storage Account:</strong> Used for two purposes:
            <ul>
              <li><strong>Blob container</strong> — <code>api-specs</code> stores YAML files</li>
              <li><strong>Table storage</strong> — <code>apispecs</code> stores spec metadata (id, label, group, fileName, shareId); <code>shares</code> table is a secondary index for share ID lookups</li>
            </ul>
          </div>
          <div className="arch-item">
            <strong>Azure Management API:</strong> Called exclusively from the backend using the managed identity's token.
          </div>
        </div>
      </section>

      {/* Prerequisites */}
      <section className="setup-section">
        <h3>✅ Prerequisites</h3>
        <ul className="checklist">
          <li>Azure subscription with appropriate permissions</li>
          <li>Logic Apps already deployed in a resource group</li>
          <li>An Azure Storage Account (general-purpose v2) — used for blobs and tables</li>
          <li>VS Code with Azure Functions extension installed</li>
          <li>.NET 8 SDK installed</li>
          <li>Azure Functions Core Tools v4 installed (for local development)</li>
        </ul>
      </section>

      {/* Deployment Steps */}
      <section className="setup-section">
        <h3>🚀 Deployment Steps</h3>

        <div className="step-card">
          <div className="step-number">1</div>
          <div className="step-content">
            <h4>Deploy the LogicAppsApi Function App</h4>
            <p>Using VS Code Command Palette (<code>Ctrl+Shift+P</code>):</p>
            <ol>
              <li>Run: <code>Azure Functions: Deploy to Function App...</code></li>
              <li>Browse to the <code>LogicAppsApi</code> folder</li>
              <li>Select <strong>Create new Function App in Azure (Advanced)</strong></li>
              <li>Name: <strong>LogicAppsApi</strong> (or your preferred name)</li>
              <li>Runtime: <strong>.NET 8 (Isolated)</strong></li>
              <li>OS: <strong>Windows</strong></li>
              <li>Hosting plan: <strong>Consumption</strong></li>
              <li>Resource group: select or create one</li>
              <li>Storage account: create new or use existing (note its name — you'll need it)</li>
              <li>Wait for deployment to complete</li>
            </ol>
          </div>
        </div>

        <div className="step-card">
          <div className="step-number">2</div>
          <div className="step-content">
            <h4>Enable Managed Identity on LogicAppsApi</h4>
            <p>In Azure Portal → Function Apps → LogicAppsApi:</p>
            <div className="config-section">
              <h5>Identity → System assigned</h5>
              <ol>
                <li>Set Status to <strong>On</strong></li>
                <li>Click <strong>Save</strong></li>
                <li>Note the <strong>Object (principal) ID</strong> — you'll need it in the next steps</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="step-card">
          <div className="step-number">3</div>
          <div className="step-content">
            <h4>Assign Reader RBAC for Logic Apps</h4>
            <p>The function app needs permission to read Logic App run history. Grant at the subscription or resource group level:</p>
            <div className="config-section">
              <h5>Option A — Resource Group level (recommended, least privilege)</h5>
              <ol>
                <li>Go to the <strong>resource group</strong> that contains your Logic Apps</li>
                <li>Click <strong>Access control (IAM)</strong></li>
                <li>Click <strong>Add role assignment</strong></li>
                <li>Role: <strong>Reader</strong></li>
                <li>Assign access to: <strong>Managed Identity</strong></li>
                <li>Select the <strong>LogicAppsApi</strong> function app</li>
                <li>Click <strong>Review + assign</strong></li>
              </ol>
            </div>
            <div className="config-section">
              <h5>Option B — Subscription level (simpler if apps span multiple resource groups)</h5>
              <ol>
                <li>Go to your <strong>Subscription</strong></li>
                <li>Click <strong>Access control (IAM)</strong> and follow the same steps as above</li>
              </ol>
            </div>
            <div className="warning-box">
              <strong>⏳ Note:</strong> RBAC role assignments can take 5–10 minutes to propagate. If you see 403 errors immediately after assigning, wait a few minutes and retry.
            </div>
          </div>
        </div>

        <div className="step-card">
          <div className="step-number">4</div>
          <div className="step-content">
            <h4>Assign Storage RBAC for API Docs &amp; Sharing</h4>
            <p>The function app needs two roles on the Storage Account to read/write specs and share links:</p>
            <div className="config-section">
              <h5>Storage Account → Access control (IAM) → Add role assignment</h5>
              <p>Repeat this process twice, once for each role:</p>
              <ol>
                <li>Go to your <strong>Storage Account</strong> in the Azure Portal</li>
                <li>Click <strong>Access control (IAM)</strong> → <strong>Add role assignment</strong></li>
                <li>
                  First assignment — Role: <strong>Storage Blob Data Contributor</strong><br />
                  Assign to: <strong>LogicAppsApi</strong> managed identity
                </li>
                <li>
                  Second assignment — Role: <strong>Storage Table Data Contributor</strong><br />
                  Assign to: <strong>LogicAppsApi</strong> managed identity
                </li>
              </ol>
            </div>
            <div className="warning-box">
              <strong>ℹ️ Note:</strong> The blob container (<code>api-specs</code>) and tables (<code>apispecs</code>, <code>shares</code>) are created automatically on first use — no manual setup needed in the portal.
            </div>
          </div>
        </div>

        <div className="step-card">
          <div className="step-number">5</div>
          <div className="step-content">
            <h4>Configure CORS on LogicAppsApi</h4>
            <p>In Azure Portal → Function Apps → LogicAppsApi → <strong>API → CORS</strong>:</p>
            <ul>
              <li>Add <code>http://localhost:3000</code> for local development</li>
              <li>Add your production frontend URL (e.g. <code>https://your-app.azurestaticapps.net</code>)</li>
            </ul>
            <div className="warning-box">
              <strong>⚠️ Important:</strong> Do not use <code>*</code> in production. The functions already handle <code>Access-Control-Allow-Origin</code> programmatically, so the portal CORS list just needs your frontend domain.
            </div>
          </div>
        </div>

        <div className="step-card">
          <div className="step-number">6</div>
          <div className="step-content">
            <h4>Configure the Frontend</h4>

            <div className="config-section">
              <h5>Step 1 — Get the function key</h5>
              <ol>
                <li>Go to Function App → <strong>App Keys</strong> → Function keys</li>
                <li>Copy the <strong>default</strong> key value — you'll use it as <code>REACT_APP_API_KEY</code> in your <code>.env</code></li>
              </ol>
            </div>

            <div className="config-section">
              <h5>Step 2 — Set <code>AZURE_STORAGE_ACCOUNT_NAME</code> on the Function App</h5>
              <p>In Azure Portal → Function App → <strong>Configuration → Application settings</strong>:</p>
              <ol>
                <li>Click <strong>New application setting</strong></li>
                <li>Name: <code>AZURE_STORAGE_ACCOUNT_NAME</code></li>
                <li>Value: the name of your storage account (e.g. <code>mycompanystorage</code>)</li>
                <li>Click <strong>Save</strong></li>
              </ol>
              <div className="warning-box">
                <strong>ℹ️ Note:</strong> This is the storage account name only (not a connection string). The function uses Managed Identity to authenticate — no keys or secrets needed.
              </div>
            </div>

            <div className="config-section">
              <h5>Step 3 — Add your Logic Apps to <code>src/logicAppsConfig.js</code></h5>
              <div className="code-block">
                <code>{'export const LOGIC_APPS = ['}</code><br />
                <code>&nbsp;&nbsp;{'{'}</code><br />
                <code>&nbsp;&nbsp;&nbsp;&nbsp;id: 'my-app',</code><br />
                <code>&nbsp;&nbsp;&nbsp;&nbsp;displayName: 'My Logic App',</code><br />
                <code>&nbsp;&nbsp;&nbsp;&nbsp;description: 'Optional description',</code><br />
                <code>&nbsp;&nbsp;&nbsp;&nbsp;subscriptionId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',</code><br />
                <code>&nbsp;&nbsp;&nbsp;&nbsp;resourceGroup: 'my-resource-group',</code><br />
                <code>&nbsp;&nbsp;&nbsp;&nbsp;workflowName: 'my-logic-app-name',</code><br />
                <code>&nbsp;&nbsp;{'}'},'</code><br />
                <code>{'];'}</code>
              </div>
              <p>You can add as many apps as needed. The values come from the Logic App's Azure portal URL or Overview blade.</p>
            </div>
          </div>
        </div>

        <div className="step-card">
          <div className="step-number">7</div>
          <div className="step-content">
            <h4>Deploy the Frontend to Azure Static Web Apps</h4>
            <div className="config-section">
              <h5>Option A — VS Code extension (easiest)</h5>
              <ol>
                <li>Install the <strong>Azure Static Web Apps</strong> VS Code extension</li>
                <li>Open the <code>LogicAppsMonitor</code> folder in VS Code</li>
                <li>Click the Azure icon → Static Web Apps → <strong>Create Static Web App...</strong></li>
                <li>Set app location to <code>/</code>, build command to <code>npm run build</code>, output location to <code>build</code></li>
                <li>The extension deploys automatically after build</li>
              </ol>
            </div>
            <div className="config-section">
              <h5>Option B — Manual build + portal upload</h5>
              <ol>
                <li>Set env vars in a <code>.env</code> file (or your CI pipeline) before building:</li>
              </ol>
              <div className="code-block">
                <code>REACT_APP_API_URL=https://logicappsapi.azurewebsites.net</code><br />
                <code>REACT_APP_API_KEY=your-default-function-key</code>
              </div>
              <ol start={2}>
                <li>Build:</li>
              </ol>
              <div className="code-block">
                <code>npm run build</code>
              </div>
              <ol start={3}>
                <li>Deploy the <code>build/</code> folder to your Static Web App or any static host</li>
              </ol>
            </div>
            <div className="warning-box">
              <strong>ℹ️ SPA routing:</strong> The <code>public/staticwebapp.config.json</code> configures Azure Static Web Apps to serve <code>index.html</code> for all routes (required for client-side routing). The <code>public/web.config</code> does the same for IIS-based hosts.
            </div>
            <div className="warning-box">
              <strong>ℹ️ MSAL redirect URI:</strong> After deploying, add your Static Web App URL (e.g. <code>https://your-app.azurestaticapps.net</code>) as a <strong>Redirect URI</strong> and <strong>Logout URL</strong> in Azure AD → App registrations → your app → Authentication.
            </div>
          </div>
        </div>
      </section>

      {/* Local Development */}
      <section className="setup-section">
        <h3>💻 Local Development</h3>
        <div className="config-section">
          <h5>Running LogicAppsApi locally</h5>
          <p><code>DefaultAzureCredential</code> picks up credentials automatically. Run <code>az login</code> first, then set <code>AZURE_STORAGE_ACCOUNT_NAME</code> in <code>local.settings.json</code>:</p>
          <div className="code-block">
            <code>{'{'}</code><br />
            <code>&nbsp;&nbsp;"IsEncrypted": false,</code><br />
            <code>&nbsp;&nbsp;"Values": {'{'}</code><br />
            <code>&nbsp;&nbsp;&nbsp;&nbsp;"AzureFunctionsJobHost__logging__logLevel__Default": "Information",</code><br />
            <code>&nbsp;&nbsp;&nbsp;&nbsp;"FUNCTIONS_WORKER_RUNTIME": "dotnet-isolated",</code><br />
            <code>&nbsp;&nbsp;&nbsp;&nbsp;"AZURE_STORAGE_ACCOUNT_NAME": "your-storage-account-name"</code><br />
            <code>&nbsp;&nbsp;{'}'}</code><br />
            <code>{'}'}</code>
          </div>
          <p>Then start the function:</p>
          <div className="code-block">
            <code>cd LogicAppsApi</code><br />
            <code>func start</code>
          </div>
          <p>The function runs on <code>http://localhost:7071</code>. Your logged-in account must have the Reader role on the target resource group, plus the two Storage roles on the storage account.</p>
        </div>
        <div className="config-section">
          <h5>Running the frontend locally</h5>
          <div className="code-block">
            <code>REACT_APP_API_URL=http://localhost:7071</code><br />
            <code>REACT_APP_API_KEY=</code><br />
            <br />
            <code>npm start</code>
          </div>
          <p>Leave <code>REACT_APP_API_KEY</code> empty — locally the functions run without a key.</p>
        </div>
      </section>

      {/* Verification */}
      <section className="setup-section">
        <h3>🔍 Verification Steps</h3>
        <ol className="verification-list">
          <li>
            <strong>Test the runs endpoint:</strong>
            <div className="code-block">
              <code>curl "https://logicappsapi.azurewebsites.net/api/runs?subscriptionId=SUB&resourceGroup=RG&workflowName=WF&startTime=2024-01-01T00:00:00Z&code=YOUR_KEY"</code>
            </div>
            You should receive a JSON object with <code>total</code>, <code>failed</code>, <code>succeeded</code>, and <code>failedRuns</code>.
          </li>
          <li>
            <strong>Test the specs endpoint:</strong>
            <div className="code-block">
              <code>curl "https://logicappsapi.azurewebsites.net/api/specs?code=YOUR_KEY"</code>
            </div>
            You should receive a JSON array of spec objects (each with <code>id</code>, <code>label</code>, <code>group</code>, <code>shareId</code>).
          </li>
          <li>
            <strong>Test creating/updating a spec:</strong>
            <div className="code-block">
              <code>
                curl -X POST "https://logicappsapi.azurewebsites.net/api/specs?code=YOUR_KEY" \<br/>
                &nbsp;&nbsp;-H "Content-Type: application/json" \<br/>
                &nbsp;&nbsp;-d '{`{"name":"Test API","yaml":"openapi: 3.0.0\\ninfo:\\n  title: Test\\n  version: 1.0.0\\npaths: {}","group":"Testing"}`}'
              </code>
            </div>
            You should receive a JSON object with <code>id</code>, <code>label</code>, <code>group</code>, <code>fileName</code>, and <code>shareId</code>. 
            Running the same command again will update the existing spec and preserve the <code>shareId</code>.
          </li>
          <li>
            <strong>Open the frontend</strong> and check that the Dashboard shows your Logic Apps with run counts.
          </li>
          <li>
            <strong>Open API Reference</strong>, add a spec, click Share — the link should appear immediately. Opening the link in an incognito window should show the docs without logging in.
          </li>
        </ol>
      </section>

      {/* Troubleshooting */}
      <section className="setup-section">
        <h3>🔧 Troubleshooting</h3>
        <div className="troubleshooting-box">
          <div className="issue">
            <strong>403 Forbidden from the function (Logic Apps):</strong>
            <ul>
              <li>Managed Identity is not enabled on the Function App — check Identity → System assigned is On</li>
              <li>Reader role assignment hasn't propagated yet — wait 5–10 minutes</li>
              <li>Role was assigned to the wrong identity (use the Object ID shown in Identity blade)</li>
            </ul>
          </div>
          <div className="issue">
            <strong>503 Service Unavailable on shares or specs endpoints:</strong>
            <ul>
              <li><code>AZURE_STORAGE_ACCOUNT_NAME</code> is not set in Function App → Configuration → Application settings</li>
              <li>Verify the value is the account name only (e.g. <code>mycompanystorage</code>), not a connection string</li>
            </ul>
          </div>
          <div className="issue">
            <strong>403 on blob or table operations (storage):</strong>
            <ul>
              <li>The managed identity is missing <strong>Storage Blob Data Contributor</strong> or <strong>Storage Table Data Contributor</strong> on the storage account</li>
              <li>Role assignments can take 5–10 minutes to propagate</li>
            </ul>
          </div>
          <div className="issue">
            <strong>401 Unauthorized (function key error):</strong>
            <ul>
              <li>Verify <code>REACT_APP_API_KEY</code> in <code>.env</code> matches the <strong>default</strong> function key in Azure Portal</li>
              <li>Keys are under Function App → App Keys → Function keys → default</li>
            </ul>
          </div>
          <div className="issue">
            <strong>CORS error in browser:</strong>
            <ul>
              <li>Add your frontend origin to the Function App → API → CORS list</li>
              <li>Include the protocol (<code>https://</code>) and no trailing slash</li>
              <li>For local dev add <code>http://localhost:3000</code></li>
            </ul>
          </div>
          <div className="issue">
            <strong>Logic App shows 0 runs or cards never load:</strong>
            <ul>
              <li>Check <code>subscriptionId</code>, <code>resourceGroup</code>, and <code>workflowName</code> in <code>logicAppsConfig.js</code> match the actual Azure resource exactly (case-sensitive)</li>
              <li>Open browser DevTools → Network to see the actual error response from the API</li>
            </ul>
          </div>
          <div className="issue">
            <strong>DefaultAzureCredential fails locally:</strong>
            <ul>
              <li>Run <code>az login</code> to authenticate via Azure CLI</li>
              <li>Ensure the account you logged in with has Reader access to the resource group and both Storage roles</li>
              <li>Alternatively set <code>AZURE_CLIENT_ID</code>, <code>AZURE_TENANT_ID</code>, <code>AZURE_CLIENT_SECRET</code> env vars in <code>local.settings.json</code></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Security Notes */}
      <section className="setup-section">
        <h3>🔒 Security Notes</h3>
        <ul className="security-list">
          <li>Managed Identity — no credentials stored, no secrets to rotate</li>
          <li>Reader role only — the function app cannot modify or delete any Logic App resources</li>
          <li>Frontend never touches the Azure Management API — no management scopes, no admin consent</li>
          <li>Function key protects the authenticated endpoints — keep it out of source control</li>
          <li>Share links (<code>/api/shares/{'{id}'}/spec</code>) are intentionally public/anonymous — only share specs that are safe for external access</li>
          <li>Always use HTTPS in production</li>
          <li>Scope RBAC to the resource group / storage account level, not the entire subscription, where possible</li>
        </ul>
      </section>

      {/* Quick Reference */}
      <section className="setup-section">
        <h3>📚 Quick Reference</h3>
        <div className="reference-grid">
          <div className="ref-card">
            <h4>Required RBAC Roles</h4>
            <ul>
              <li><strong>Logic Apps RG:</strong> Reader</li>
              <li><strong>Storage Account:</strong> Storage Blob Data Contributor</li>
              <li><strong>Storage Account:</strong> Storage Table Data Contributor</li>
            </ul>
          </div>
          <div className="ref-card">
            <h4>API Endpoints</h4>
            <ul>
              <li><code>GET /api/runs</code> — run stats</li>
              <li><code>GET /api/actions</code> — failed step details</li>
              <li><code>GET /api/specs</code> — list all specs</li>
              <li><code>POST /api/specs</code> — create or update spec</li>
              <li><code>GET /api/specs/{'{name}'}</code> — fetch spec YAML</li>
              <li><code>DELETE /api/specs/{'{name}'}</code> — delete spec</li>
              <li><code>GET /api/shares/{'{id}'}</code> — resolve share ID</li>
              <li><code>GET /api/shares/{'{id}'}/spec</code> — public spec YAML</li>
            </ul>
          </div>
          <div className="ref-card">
            <h4>Storage Resources</h4>
            <ul>
              <li>Blob container: <code>api-specs</code></li>
              <li>Table: <code>apispecs</code> (spec metadata)</li>
              <li>Table: <code>shares</code> (share ID index)</li>
            </ul>
          </div>
          <div className="ref-card">
            <h4>Environment Variables</h4>
            <ul>
              <li><strong>Function App:</strong> <code>AZURE_STORAGE_ACCOUNT_NAME</code></li>
              <li><strong>Frontend:</strong> <code>REACT_APP_API_URL</code></li>
              <li><strong>Frontend:</strong> <code>REACT_APP_API_KEY</code></li>
            </ul>
          </div>
          <div className="ref-card">
            <h4>Local Ports</h4>
            <ul>
              <li>LogicAppsApi: <code>http://localhost:7071</code></li>
              <li>Frontend: <code>http://localhost:3000</code></li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Setup;
